import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { CLOSE_ICON } from '../../../constants/images';
import {
  METRICS_TIMER_SELECTOR,
  PERIOD_LAST_THREE_MONTHS,
  PERIOD_LAST_TWELVE_MONTHS,
  PERIOD_SELECTED_MONTH
} from '../../../constants/metrics';
import { CLICK_ACTION, TIME_FRAME_METRICS, TIMER_SELECTOR_FILTER_DICT } from '../../../constants/tagManager';
import useOutsideComponentClick from '../../../hooks/useOutsideComponentClick/outsideComponentClick';
import { IPeriodSelection } from '../../../interfaces/metrics';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { GenericVoidFunction } from '../../../types/genericTypes';
import { capitalize } from '../../../utils/utils';
import BdsRadioButton from '../../v3/BdsRadioButton/BdsRadioButton';
import BdsButton from '../Buttons/BdsButton/BdsButton';
import BdsIconButton from '../Buttons/BdsIconButton/BdsIconButton';
import BdsDivider from '../Globals/BdsDivider/BdsDivider';
import './TimerSelector.scss';

interface ITimerSelector {
  showTimerSelector: boolean;
  setShowTimerSelector: GenericVoidFunction;
  onSelected: (value: IPeriodSelection) => void;
  initialValue?: IPeriodSelection;
  showAllMonths?: boolean;
  showAggregatedPeriods?: boolean;
}

interface IMonth {
  label: string;
  value: string;
}

const period = {
  LAST_3: PERIOD_LAST_THREE_MONTHS,
  LAST_12: PERIOD_LAST_TWELVE_MONTHS,
  MONTH: PERIOD_SELECTED_MONTH
};

const TimerSelector = ({
  showTimerSelector,
  setShowTimerSelector,
  onSelected,
  initialValue,
  showAllMonths = true,
  showAggregatedPeriods = true
}: ITimerSelector) => {
  const [state, setState] = useState<{
    selectedPeriod: string | null;
    selectedMonth: string | null;
    displayedMonths: IMonth[];
  }>({
    selectedPeriod: null,
    selectedMonth: null,
    displayedMonths: []
  });

  const timerSelectorRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const getMonths = useCallback(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    const monthsToShow = showAllMonths ? 12 : 1;

    return Array.from({ length: monthsToShow }, (_, i) => {
      const d = new Date(currentYear, currentDate.getMonth() - i, 1);
      if (d.getMonth() > currentMonth) {
        d.setFullYear(currentYear - 1);
      }
      const year = d.getFullYear();
      const monthName = t(`csp.months.${d.toLocaleString('en-US', { month: 'long' }).toLowerCase()}`);
      const formattedMonth = String(d.getMonth() + 1).padStart(2, '0');
      const formattedDate = `${year}-${formattedMonth}-01`;
      const yearDate = d.getFullYear() === currentYear ? '' : ` ${d.getFullYear()}`;
      return {
        label: monthName + yearDate,
        value: formattedDate
      };
    });
  }, [t]);

  useEffect(() => {
    const months = getMonths();
    setState((prevState) => ({ ...prevState, displayedMonths: months }));
  }, [getMonths]);

  useEffect(() => {
    if (!showAggregatedPeriods && state.selectedPeriod !== period.MONTH && state.displayedMonths.length) {
      setState((prevState) => ({
        ...prevState,
        selectedPeriod: period.MONTH,
        selectedMonth: state.displayedMonths[0].label ?? null
      }));
      onSelected({ period: PERIOD_SELECTED_MONTH, month: state.displayedMonths[0].value });
    }
  }, [showAggregatedPeriods]);

  useEffect(() => {
    if (initialValue) {
      const { period, month } = initialValue;
      if (month) {
        const selectedMonth = state.displayedMonths.find((m) => m.value === month);
        if (selectedMonth) {
          setState((prevState) => ({
            ...prevState,
            selectedPeriod: period,
            selectedMonth: selectedMonth.label
          }));
        }
      } else {
        setState((prevState) => ({
          ...prevState,
          selectedPeriod: period,
          selectedMonth: null
        }));
      }
    }
  }, [initialValue, state.displayedMonths]);

  const handleRadioChange = useCallback(
    (month: IMonth) => {
      if (state.selectedPeriod === period.MONTH && state.selectedMonth === month.label) {
        return;
      }
      setState((prevState) => ({
        ...prevState,
        selectedPeriod: period.MONTH,
        selectedMonth: month.label
      }));
      onSelected({ period: PERIOD_SELECTED_MONTH, month: month.value });
    },
    [state.selectedPeriod, state.selectedMonth, onSelected]
  );

  const handleSelectPeriod = useCallback(
    (newPeriod: string, month: IMonth | null = null) => {
      if (
        newPeriod === state.selectedPeriod &&
        (newPeriod !== period.MONTH || (month && state.selectedMonth === month.label))
      ) {
        return;
      }

      setState((prevState) => ({
        ...prevState,
        selectedPeriod: newPeriod,
        selectedMonth: month?.label ?? null
      }));

      TagManagerService.pushEvent(CLICK_ACTION, TIME_FRAME_METRICS, TIMER_SELECTOR_FILTER_DICT[newPeriod]);

      if (newPeriod === period.MONTH && month) {
        onSelected({ period: PERIOD_SELECTED_MONTH, month: month.value });
      } else if (newPeriod === period.LAST_3) {
        onSelected({ period: PERIOD_LAST_THREE_MONTHS });
      } else if (newPeriod === period.LAST_12) {
        onSelected({ period: PERIOD_LAST_TWELVE_MONTHS });
      }
    },
    [state.selectedPeriod, state.selectedMonth, onSelected]
  );

  const handleClose = useCallback(() => {
    setShowTimerSelector(false);
  }, [setShowTimerSelector]);

  useOutsideComponentClick(timerSelectorRef, handleClose);

  return (
    <div className={`timer-selector-container ${showTimerSelector ? 'shown' : ''}`} ref={timerSelectorRef}>
      <div className="timer-selector-icon-close">
        <BdsIconButton
          icon={CLOSE_ICON}
          action={handleClose}
          variant="transparent"
          size="sm"
          label="timer-close-icon"
        />
      </div>
      <h2 className="timer-selector-title">{t(METRICS_TIMER_SELECTOR.title)}</h2>
      <div className="timer-selector-months">
        {state.displayedMonths.map((month, index) => (
          <button
            tabIndex={0}
            key={month.label}
            className="timer-selector-months-month-item"
            onClick={() => handleSelectPeriod(period.MONTH, month)}
          >
            <div className="timer-selector-months-month-item-label">
              <p
                className={
                  state.selectedPeriod === period.MONTH && state.selectedMonth === month.label ? 'month-selected' : ''
                }
              >
                {capitalize(month.label)}
              </p>
              <BdsRadioButton
                variant="primary"
                onChange={() => handleRadioChange(month)}
                selected={state.selectedPeriod === period.MONTH && state.selectedMonth === month.label}
                value={month.label}
                name={month.label}
              />
            </div>
            {(index === 0 || (index + 2) % 2 === 0) && <BdsDivider size="s" color="subtle" />}
          </button>
        ))}
      </div>
      <BdsDivider size={showAllMonths ? 'm' : 's'} color="subtle" />
      {showAggregatedPeriods && (
        <div className="timer-selector-actions">
          <BdsButton
            action={() => handleSelectPeriod(period.LAST_3)}
            variant={state.selectedPeriod === period.LAST_3 ? 'primary' : 'secondary'}
            size="s"
          >
            {t(METRICS_TIMER_SELECTOR.lastThreeMonths)}
          </BdsButton>
          <BdsButton
            action={() => handleSelectPeriod(period.LAST_12)}
            variant={state.selectedPeriod === period.LAST_12 ? 'primary' : 'secondary'}
            size="s"
          >
            {t(METRICS_TIMER_SELECTOR.lastTwelveMonths)}
          </BdsButton>
        </div>
      )}
    </div>
  );
};

export default TimerSelector;
